import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { HugeiconsIcon } from '@hugeicons/react'
import { Download02Icon } from '@hugeicons-pro/core-bulk-rounded'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import PropTypes from 'prop-types'

import { selectIsOnFreePlan } from '@/app/slices/organisationSlice'
import { useToolBoxTreatments } from '@/common/hooks/useToolBoxTreatments'
import { ORDER_DOWNLOAD_DOCUMENTS_OPTIONS, Paths, PUBLIC_DOCUMENTS } from '@/common/utils'

const DownloadDocuments = ({
    autoExport = false,
    color = 'primary',
    disableElevation = false,
    disabled = false,
    order,
    sx = null,
    useIconButton = false,
    variant = 'contained',
}) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const { showDownloadPdfs } = useToolBoxTreatments()

    const isOnFreePlan = useSelector(selectIsOnFreePlan)

    const [downloadAnchorEl, setDownloadAnchorEl] = useState(null)

    const downloadMenuOpen = Boolean(downloadAnchorEl)

    const orderStatus = order?.status

    const handleDownloadClick = (event) => {
        setDownloadAnchorEl(event.currentTarget)
    }

    const handleDownloadClose = () => {
        setDownloadAnchorEl(null)
    }

    const handlePublicDocumentsClick = (orderId, document) => {
        const documentPath = Paths.DOCUMENTS_PATHNAME.replace(':orderId', orderId)
            .replace(':documentType', document)
            .replace(':action?', autoExport ? 'export' : '')

        // A new tab keeps the order page open behind the document.
        window.open(documentPath, '_blank', 'noopener')

        handleDownloadClose()
    }

    const downloadOptions = ORDER_DOWNLOAD_DOCUMENTS_OPTIONS[orderStatus]

    const menuItems = useMemo(() => {
        return downloadOptions?.map((option) => {
            const { disabled, hidden, id, label, tagAttrs, type } = option
            return {
                id,
                label,
                disabled,
                hidden: hidden || type === 'private',
                type,
                tagAttrs,
            }
        })
    }, [downloadOptions])

    return showDownloadPdfs ? (
        <Box sx={[...(Array.isArray(sx) ? sx : [sx])]}>
            {useIconButton ? (
                <IconButton
                    aria-controls={downloadMenuOpen ? 'download-menu' : undefined}
                    aria-expanded={downloadMenuOpen ? 'true' : undefined}
                    aria-haspopup="true"
                    disabled={disabled}
                    id="download-button"
                    size="small"
                    title={t('Download documents')}
                    onClick={handleDownloadClick}
                >
                    <HugeiconsIcon
                        color={theme.palette.text.primary}
                        icon={Download02Icon}
                        size={20}
                    />
                </IconButton>
            ) : (
                <Button
                    aria-controls={downloadMenuOpen ? 'download-menu' : undefined}
                    aria-expanded={downloadMenuOpen ? 'true' : undefined}
                    aria-haspopup="true"
                    color={color}
                    disabled={disabled}
                    disableElevation={disableElevation}
                    endIcon={
                        <HugeiconsIcon
                            color={theme.palette.primary.contrastText}
                            icon={Download02Icon}
                            size={20}
                        />
                    }
                    id="download-button"
                    variant={variant}
                    onClick={handleDownloadClick}
                >
                    {t('Download')}
                </Button>
            )}

            <Menu
                anchorEl={downloadAnchorEl}
                id="download-menu"
                MenuListProps={{
                    'aria-labelledby': 'download-button',
                }}
                open={downloadMenuOpen}
                onClose={handleDownloadClose}
            >
                <Typography
                    component="li"
                    key="title"
                    sx={{
                        px: 2,
                        py: 0.75,
                    }}
                    variant="strong1"
                >
                    {t('Download documents')}
                </Typography>

                <Divider key="divider1" />

                {menuItems?.map(
                    (menuItem) =>
                        !menuItem.hidden && [
                            <MenuItem
                                disabled={menuItem.disabled || (isOnFreePlan && menuItem.id !== PUBLIC_DOCUMENTS.Quote)}
                                key={menuItem.id}
                                onClick={() => handlePublicDocumentsClick(order?.id, menuItem.id)}
                                {...menuItem.tagAttrs}
                            >
                                {t(menuItem.label)}
                            </MenuItem>,
                        ]
                )}
            </Menu>
        </Box>
    ) : null
}

DownloadDocuments.propTypes = {
    autoExport: PropTypes.bool,
    color: PropTypes.string,
    disabled: PropTypes.bool,
    disableElevation: PropTypes.bool,
    order: PropTypes.shape({
        id: PropTypes.string,
        isDeleted: PropTypes.bool,
        name: PropTypes.string,
        orderItems: PropTypes.array,
        status: PropTypes.string,
    }),
    sx: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])),
        PropTypes.func,
        PropTypes.object,
    ]),
    useIconButton: PropTypes.bool,
    variant: PropTypes.string,
}

export default DownloadDocuments
